import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import type { PasswordStrength } from '../../lib/passwordRules';

/**
 * The field / error / submit vocabulary shared by the four auth screens.
 *
 * Presentational only — nothing in this file imports `api`, and nothing in it
 * runs an effect. That is deliberate: the set-password screens must fire
 * nothing on mount, and a shared component is exactly where a "convenient"
 * mount-time fetch would hide from the pins that guard those pages.
 */

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  /** Copy under the field. Rendered red when `hintIsError`. */
  hint?: string | null;
  hintIsError?: boolean;
};

export const AuthTextField = forwardRef<HTMLInputElement, FieldProps>(function AuthTextField(
  { label, hint, hintIsError, id, ...inputProps },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <div className="auth-field">
      <label className="auth-label" htmlFor={fieldId}>
        {label}
      </label>
      <input ref={ref} id={fieldId} className="auth-input" {...inputProps} />
      {hint ? <p className={`auth-hint${hintIsError ? ' is-error' : ''}`}>{hint}</p> : null}
    </div>
  );
});

/**
 * Password field with a show/hide toggle.
 *
 * The toggle flips the input's `type` in place rather than swapping in a second
 * element, so a password manager that has already attached to this input keeps
 * its grip — Joey uses one, and an input that gets unmounted and remounted mid-
 * fill is how autofill silently stops working.
 */
export const AuthPasswordField = forwardRef<HTMLInputElement, FieldProps>(
  function AuthPasswordField({ label, hint, hintIsError, id, ...inputProps }, ref) {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const [revealed, setRevealed] = useState(false);

    return (
      <div className="auth-field">
        <label className="auth-label" htmlFor={fieldId}>
          {label}
        </label>
        <div className="auth-input-wrap">
          <input
            ref={ref}
            id={fieldId}
            className="auth-input"
            type={revealed ? 'text' : 'password'}
            {...inputProps}
          />
          <button
            type="button"
            className="auth-reveal"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            title={revealed ? 'Hide password' : 'Show password'}
            // Skipped in the tab order on purpose: it sits between the password
            // field and the submit button, and tabbing into a visibility toggle
            // on the way to signing in is friction for everyone to serve the
            // rare case. It stays reachable by click and by screen reader.
            tabIndex={-1}
          >
            {revealed ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
          </button>
        </div>
        {hint ? <p className={`auth-hint${hintIsError ? ' is-error' : ''}`}>{hint}</p> : null}
      </div>
    );
  },
);

/**
 * The ONE error region per screen, above the submit.
 *
 * `assertive` rather than `polite` because the user just pressed a button and
 * is waiting on the answer; a polite region would queue behind whatever else
 * the page is saying and arrive after they have already retyped the password.
 * `tabIndex={-1}` exists so the page can move focus here — see the pages.
 */
export const AuthErrorRegion = forwardRef<HTMLDivElement, { id: string; children: ReactNode }>(
  function AuthErrorRegion({ id, children }, ref) {
    return (
      <div
        ref={ref}
        id={id}
        className="auth-error"
        role="alert"
        aria-live="assertive"
        tabIndex={-1}
      >
        {children}
      </div>
    );
  },
);

/**
 * The single accent action. Never disabled while busy — a dimmed primary reads
 * as broken, and the wait is genuine server-side work (scrypt is ~140ms
 * measured, plus the round trip), not a stall. Double-submit is guarded in the
 * handler instead.
 */
export function AuthSubmit({
  busy,
  label,
  busyLabel,
}: {
  busy: boolean;
  label: string;
  busyLabel: string;
}) {
  return (
    <button type="submit" className="btn btn-cta auth-submit" aria-busy={busy}>
      {busy ? (
        <>
          <span className="auth-spinner" aria-hidden />
          {busyLabel}
        </>
      ) : (
        label
      )}
    </button>
  );
}

const STRENGTH_CLASS: Record<PasswordStrength, string> = {
  none: '',
  weak: 'is-weak',
  fair: 'is-fair',
  strong: 'is-strong',
};

const STRENGTH_LABEL: Record<PasswordStrength, string> = {
  none: `At least 12 characters. A short phrase beats a mangled word.`,
  weak: 'Too weak',
  fair: 'Good — longer is better',
  strong: 'Strong',
};

/**
 * 4px advisory bar. It reflects the verdict; it never gates the submit, because
 * NIST's rule is a length floor and everything past that is a suggestion.
 */
export function PasswordStrengthBar({ strength }: { strength: PasswordStrength }) {
  return (
    <div className="auth-strength">
      <div className="auth-strength-track">
        <div className={`auth-strength-fill ${STRENGTH_CLASS[strength]}`.trim()} />
      </div>
      <p className="auth-strength-label">{STRENGTH_LABEL[strength]}</p>
    </div>
  );
}
